import { useAuth } from '@/hooks/useAuth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

function AccessDeniedPage() {
  const { t } = useTranslation('auth');
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const { login } = useAuth();
  const handleContactSupport = () => {
    window.open('https://support.expcloud.com/portal/en/newticket', '_blank');
  };

  return (
    <Box
      data-testid="access-denied"
      sx={{
        backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark}15, ${theme.palette.background.default}, ${theme.palette.secondary.light}10)`,
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
        textAlign: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Background pattern */}
      <Box
        data-testid="access-denied-background-pattern"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage:
            'radial-gradient(circle, currentColor 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          zIndex: 1,
        }}
      />

      <Box
        data-testid="access-denied-content-card"
        sx={{
          maxWidth: 550,
          width: '100%',
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? `${theme.palette.background.paper}dd`
              : `${theme.palette.common.white}f7`,
          boxShadow:
            '0 10px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
          zIndex: 2,
          transition: 'all 0.3s ease-in-out',
          animation: 'fadeIn 0.5s ease-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        {/* Lock or Shield Icon */}
        <Box
          data-testid="access-denied-icon-container"
          sx={{
            mb: 3,
            mt: -2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            component="div"
            data-testid="access-denied-icon"
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${theme.palette.error.main}15`,
              color: theme.palette.error.main,
              mb: 2,
            }}
          >
            <svg
              data-testid="access-denied-shield-icon"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.93V12H5V6.3L12 3.19V11.99Z"
                fill="currentColor"
              />
            </svg>
          </Box>
        </Box>

        <Typography
          data-testid="access-denied-title"
          variant="h3"
          color="error"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
            letterSpacing: '-0.5px',
          }}
        >
          {t('accessDenied.title')}
        </Typography>

        <Typography
          data-testid="access-denied-message"
          variant="body1"
          sx={{
            mb: 3,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            lineHeight: 1.5,
            maxWidth: '450px',
            mx: 'auto',
            color: theme.palette.text.primary,
          }}
        >
          {t('accessDenied.message')}
        </Typography>

        <Box
          data-testid="access-denied-support-info"
          sx={{
            p: 2,
            mt: 2,
            mb: 4,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.02)',
            borderRadius: 2,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
          }}
        >
          <Typography
            data-testid="access-denied-support-text"
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            {t('accessDenied.contactSupport')}
          </Typography>
        </Box>

        <Box
          data-testid="access-denied-buttons-container"
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Box
            data-testid="access-denied-return-button"
            onClick={login}
            sx={{
              display: 'inline-block',
              px: 4,
              py: 1.5,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              cursor: 'pointer',
              borderRadius: 2,
              textDecoration: 'none',
              fontWeight: 'medium',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {t('accessDenied.returnButton')}
          </Box>

          <Box
            data-testid="access-denied-support-button"
            onClick={handleContactSupport}
            sx={{
              display: 'inline-block',
              cursor: 'pointer',
              px: 4,
              py: 1.5,
              bgcolor: 'transparent',
              color: theme.palette.text.primary,
              borderRadius: 2,
              textDecoration: 'none',
              fontWeight: 'medium',
              fontSize: '0.95rem',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.03)',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(0,0,0,0.2)',
              },
            }}
          >
            {t('accessDenied.supportButton')}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Typography
        data-testid="access-denied-footer"
        variant="caption"
        color="text.secondary"
        sx={{
          position: 'absolute',
          bottom: 16,
          opacity: 0.7,
          zIndex: 2,
        }}
      >
        {t('accessDenied.copyright', { year: currentYear })}
      </Typography>
    </Box>
  );
}

export default AccessDeniedPage;
